import { useState, useEffect, useCallback } from 'react';
import { mockAnalyticsAPI, MockAnalyticsData, MockGuardrailData, MockAdvancedData } from '../services/mockData.ts';
import { toast } from 'react-hot-toast';

export const useAnalytics = () => {
  const [summary, setSummary] = useState<MockAnalyticsData | null>(null);
  const [realtimeStats, setRealtimeStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasData, setHasData] = useState(false);

  const fetchAnalyticsSummary = useCallback(async (timeframe: number = 24) => {
    try {
      setLoading(true);
      setError(null);
      const data = await mockAnalyticsAPI.getAnalyticsSummary(timeframe);
      setSummary(data);
      setHasData(true);
      setIsLoaded(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch analytics summary';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRealtimeStats = useCallback(async () => {
    try {
      const data = await mockAnalyticsAPI.getRealtimeStats();
      setRealtimeStats(data);
    } catch (err) {
      console.error('Failed to fetch realtime stats:', err);
    }
  }, []);

  const exportAnalyticsData = useCallback(async (timeframe: number = 24, comprehensive: boolean = false) => {
    try {
      const data = comprehensive ? 
        await mockAnalyticsAPI.getAnalyticsSummary(timeframe) :
        { summary: await mockAnalyticsAPI.getAnalyticsSummary(timeframe) };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Analytics data exported successfully');
    } catch (err) {
      toast.error('Failed to export analytics data');
    }
  }, []);

  const exportAnalyticsToExcel = useCallback(async (timeframe: number = 24, comprehensive: boolean = false) => {
    try {
      const data = await mockAnalyticsAPI.getAnalyticsSummary(timeframe);
      
      // Create CSV data
      const csvData = [
        ['Analytics Export'],
        ['Generated:', new Date().toISOString()],
        ['Timeframe:', `${timeframe} hours`],
        [''],
        ['SUMMARY'],
        ['Metric', 'Value'],
        ['Total Requests', data.total_requests],
        ['Blocked Requests', data.blocked_requests],
        ['Allowed Requests', data.allowed_requests],
        ['Block Rate (%)', data.block_rate.toFixed(2)],
        ['Unique Users', data.unique_users],
        ['Unique IPs', data.unique_ips],
        ['Avg Response Time (ms)', data.avg_response_time],
        ['Error Rate (%)', data.error_rate.toFixed(2)],
        [''],
        ['DETECTOR STATS'],
        ['Detector', 'Detections', 'Block Rate (%)', 'Avg Confidence (%)'],
        ...data.detector_stats.map(detector => [
          detector.name,
          detector.total_detections,
          detector.block_rate.toFixed(1),
          (detector.avg_confidence * 100).toFixed(1)
        ]),
        [''],
        ['TOP BLOCKING REASONS'],
        ['Reason', 'Count'],
        ...data.top_blocking_reasons.map(reason => [reason.reason, reason.count])
      ];

      const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Analytics data exported to Excel format');
    } catch (err) {
      toast.error('Failed to export analytics data');
    }
  }, []);

  // Calculate derived metrics
  const blockRate = summary ? summary.block_rate : 0;
  const safetyEffectiveness = summary ? Math.max(0, 100 - summary.block_rate) : 0;

  useEffect(() => {
    fetchAnalyticsSummary();
    fetchRealtimeStats();
  }, [fetchAnalyticsSummary, fetchRealtimeStats]);

  return {
    summary,
    realtimeStats,
    loading,
    error,
    fetchAnalyticsSummary,
    fetchRealtimeStats,
    exportAnalyticsData,
    exportAnalyticsToExcel,
    isLoaded,
    hasData,
    blockRate,
    safetyEffectiveness,
  };
};

export const useGuardrailAnalytics = () => {
  const [guardrailData, setGuardrailData] = useState<MockGuardrailData | null>(null);
  const [hardwareData, setHardwareData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGuardrailData = useCallback(async (hours: number = 24) => {
    try {
      setLoading(true);
      setError(null);
      const data = await mockAnalyticsAPI.getGuardrailData(hours);
      setGuardrailData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch guardrail data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHardwareData = useCallback(async () => {
    try {
      const data = await mockAnalyticsAPI.getHardwareData();
      setHardwareData(data);
    } catch (err) {
      console.error('Failed to fetch hardware data:', err);
    }
  }, []);

  const fetchAllData = useCallback(async (hours: number = 24) => {
    setLoading(true);
    await Promise.all([fetchGuardrailData(hours), fetchHardwareData()]);
    setLoading(false);
  }, [fetchGuardrailData, fetchHardwareData]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    guardrailData,
    hardwareData,
    loading,
    error,
    fetchAllData,
    fetchGuardrailData,
    fetchHardwareData,
  };
};

export const useAdvancedAnalytics = () => {
  const [dashboardData, setDashboardData] = useState<MockAdvancedData | null>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [threatAnalysis, setThreatAnalysis] = useState<any>(null);
  const [mlInsights, setMlInsights] = useState<any>(null);
  const [predictions, setPredictions] = useState<any>(null);
  const [executiveSummary, setExecutiveSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAdvancedData = useCallback(async (timeRange: number = 24) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await mockAnalyticsAPI.getAdvancedData(timeRange);
      setDashboardData(data);
      setPerformanceData({
        response_times: {
          distribution: { fast: 70, medium: 25, slow: 5 },
          p95_ms: 150,
          p99_ms: 300,
        },
        resource_utilization: {
          cpu_utilization: Math.random() * 40 + 30,
          memory_utilization: Math.random() * 50 + 30,
          queue_utilization: Math.random() * 30 + 20,
        },
        detector_performance: Object.entries(data.advanced_metrics.detector_efficiency).map(([name, perf]: [string, any]) => ({
          name,
          accuracy: perf.accuracy * 100,
          precision: Math.random() * 20 + 80,
          recall: Math.random() * 20 + 80,
          f1_score: Math.random() * 20 + 80,
          processing_time_ms: Math.floor(Math.random() * 100) + 50,
          volume: perf.volume,
        })),
      });
      setThreatAnalysis({
        threat_overview: {
          threat_level: data.threat_intelligence.overall_threat_level,
          active_campaigns: Math.floor(Math.random() * 10) + 2,
        },
        severity_distribution: data.threat_intelligence.threat_distribution,
        recommendations: [
          'Monitor suspicious IP patterns',
          'Enhance PII detection capabilities',
          'Implement additional rate limiting',
        ],
      });
      setMlInsights(data.ml_insights);
      setPredictions(data.predictions);
      setExecutiveSummary(data.executive_summary);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch advanced analytics';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdvancedData();
  }, [fetchAdvancedData]);

  return {
    dashboardData,
    performanceData,
    threatAnalysis,
    mlInsights,
    predictions,
    executiveSummary,
    loading,
    error,
    fetchAdvancedData,
  };
}; 